import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Image,
    Text,
    VStack,
    Heading,
    useToast,
    useTheme,
} from "@chakra-ui/react";

function Signin() {
    const { login } = useContext(AuthContext);
    const [emailaddress, setEmailAddress] = useState("desh2@basrus.co.za");
    const [userpass, setUserPass] = useState("1234");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const toast = useToast();
    const theme = useTheme();

    const handleLogin = async () => {
        try {
            const credentials = { username: emailaddress, password: userpass };
            const defaultRoute = await login(credentials);
            navigate(defaultRoute);
        } catch (err) {
            setError(
                `Signin failed: ${
                    err.response ? err.response.data.message : err.message
                }`
            );
            toast({
                title: "Signin Failed",
                description: err.response
                    ? err.response.data.message
                    : err.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const logoSrc = theme?.logo?.signin?.src;
    const logoAlt = theme?.logo?.signin?.alt;
    const logoWidth = theme?.logo?.signin?.width;
    const logoHeight = theme?.logo?.signin?.height;
    const logoMarginB = theme?.logo?.signin?.marginBottom;
    const isSplitHero = theme?.signin?.layout === "splitHero";

    const formFields = (
        <VStack spacing={4} align="stretch" w="100%" maxW="320px">
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
            <FormControl id="email" isInvalid={!!error}>
                <FormLabel>Email address</FormLabel>
                <Input
                    type="email"
                    placeholder="Email address"
                    value={emailaddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
            </FormControl>
            <FormControl id="password" isInvalid={!!error}>
                <FormLabel>Password</FormLabel>
                <Input
                    type="password"
                    placeholder="Password"
                    value={userpass}
                    onChange={(e) => setUserPass(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
            </FormControl>
            <Button variant="dashboardButton" onClick={handleLogin} w="100%">
                Signin
            </Button>
        </VStack>
    );

    if (isSplitHero) {
        const {
            heroImage,
            eyebrow,
            headlineBefore,
            accentPhrase,
            headlineAfter,
            body,
        } = theme.signin;
        const whiteLogo = theme.logo?.white;

        return (
            <Flex
                direction={{ base: "column", lg: "row" }}
                minH="100vh"
                w="100%"
            >
                {/* Left: Psytech hero panel */}
                <Box
                    position="relative"
                    flex={{ base: "none", lg: "1.3" }}
                    minH={{ base: "280px", md: "360px", lg: "100vh" }}
                    overflow="hidden"
                    bg="primary"
                >
                    <Box
                        position="absolute"
                        inset={0}
                        bgImage={`url(${heroImage})`}
                        bgSize="cover"
                        bgPosition="center"
                    />
                    <Box
                        position="absolute"
                        inset={0}
                        bgGradient="linear(to-r, #022433ee, #02243399, #04496555)"
                    />
                    <Box
                        position="absolute"
                        inset={0}
                        bgGradient="linear(to-t, #022433cc, transparent 55%)"
                    />
                    <Flex
                        position="relative"
                        direction="column"
                        justify="center"
                        h="100%"
                        px={{ base: 6, md: 10, lg: 14 }}
                        py={{ base: 8, lg: 12 }}
                        maxW="640px"
                    >
                        {whiteLogo?.src && (
                            <Image
                                src={whiteLogo.src}
                                alt={whiteLogo.alt}
                                width={whiteLogo.width || "160px"}
                                height={whiteLogo.height || "auto"}
                                mb={6}
                            />
                        )}
                        <Flex
                            align="center"
                            gap={2}
                            mb={5}
                            px={3}
                            py={1.5}
                            bg="whiteAlpha.200"
                            border="1px solid"
                            borderColor="whiteAlpha.400"
                            borderRadius="full"
                            w="fit-content"
                            backdropFilter="blur(8px)"
                        >
                            <Box
                                w="8px"
                                h="8px"
                                borderRadius="full"
                                bg="secondary1"
                            />
                            <Text
                                fontSize="sm"
                                fontWeight="600"
                                color="white"
                                letterSpacing="wide"
                            >
                                {eyebrow}
                            </Text>
                        </Flex>
                        <Heading
                            as="h1"
                            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                            fontWeight="700"
                            color="white"
                            lineHeight="1.2"
                            mb={5}
                        >
                            {headlineBefore}
                            <Text as="span" color="secondary1">
                                {accentPhrase}
                            </Text>
                            {headlineAfter}
                        </Heading>
                        <Text
                            color="whiteAlpha.900"
                            fontSize={{ base: "md", lg: "lg" }}
                            lineHeight="1.7"
                            maxW="520px"
                            display={{ base: "none", md: "block" }}
                        >
                            {body}
                        </Text>
                    </Flex>
                </Box>

                {/* Right: Sign-in form */}
                <Flex
                    flex={{ base: "1", lg: "1" }}
                    direction="column"
                    align="center"
                    justify="center"
                    bg="white"
                    px={{ base: 6, md: 10 }}
                    py={{ base: 10, lg: 12 }}
                >
                    <Image
                        src={logoSrc}
                        alt={logoAlt}
                        width={logoWidth}
                        height={logoHeight}
                        mb={logoMarginB || 8}
                    />
                    <Heading
                        mb={8}
                        fontSize="2xl"
                        color="primary"
                        fontWeight="600"
                    >
                        Sign in
                    </Heading>
                    {formFields}
                </Flex>
            </Flex>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100vh"
            bg="rgb(250, 250, 250)"
        >
            <Image
                src={logoSrc}
                alt={logoAlt}
                width={logoWidth}
                height={logoHeight}
                mb={logoMarginB}
            />
            <Heading mb="8">Signin</Heading>
            {formFields}
        </Box>
    );
}

export default Signin;
